<template>
  <header>
    <a-form class="form" layout="vertical" ref="formRef" :model="formState" :rules="rules">
      <a-form-item v-if="user?.customer?.type_business == 1" label="Empresa" name="entity" required>
        <entity-picker
          :showAll="false"
          defaultBehavior
          :value="formState.entity"
          @update:value="formState.entity = $event"
        />
      </a-form-item>

      <a-form-item label="Campaña" name="portfolio" required>
        <campaign-picker
          :showAll="false"
          :value="formState.portfolio"
          @update:value="formState.portfolio = $event"
          :entity="formState.entity"
        />
      </a-form-item>

      <a-form-item v-if="user?.customer?.enable_bases" label="Base" name="base" required>
        <bases-picker
          defaultBehavior
          :value="formState.base"
          @update:value="formState.base = $event"
          :entity="formState.entity"
          :portfolio="formState.portfolio"
        />
      </a-form-item>

      <a-form-item label="Mes" name="month" required>
        <a-month-picker
          v-model:value="formState.month"
          :disabled-date="disableStartDate"
          format="MM/YYYY"
          valueFormat="YYYY-MM"
          placeholder="-- / ----"
          @openChange="startOpenChange"
        />
      </a-form-item>

      <a-form-item label="TMO o Contestadas" name="type" required>
        <a-select
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          v-model:value="formState.type"
          placeholder="Seleccione"
        >
          <a-select-option value="tmo">TMO</a-select-option>
          <a-select-option value="atendidas">Contestadas</a-select-option>
        </a-select>
      </a-form-item>
    </a-form>

    <div class="actions">
      <a-tooltip placement="top">
        <template #title>
          <span v-if="isGlobalDownloading">Tiene una descarga pendiente</span>
          <span v-else>
            Se descargarán únicamente los datos de la tabla de acuerdo a los filtros seleccionados.
          </span>
        </template>

        <a-button
          type="link"
          :disabled="isButtonDisabled || isGlobalDownloading"
          :loading="isDownloading"
          @click="handleDownload"
        >
          Descargar
          <DownloadOutlined />
        </a-button>
      </a-tooltip>

      <a-button type="primary" :disabled="isButtonDisabled || loadingSearch" :loading="loadingSearch" @click="handleSearch">
        Buscar
        <SearchOutlined />
      </a-button>
    </div>
  </header>

  <a-table
    size="middle"
    v-if="formState.month"
    :scroll="scrollStyle"
    :columns="tableColumns"
    :data-source="reportData?.results"
    :loading="isTableLoading"
    :pagination="pagination"
    @change="handleTableChange"
  >
    <template #dayContent="{ text }">
      {{ text ?? 0 }}
    </template>

    <template #totalContent="{ text }">
      {{ String(text).length > 4 ? text?.toFixed(2) : text }}
    </template>
  </a-table>
</template>

<script setup>
import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons-vue";

import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { fetchUserManagementType, downloadUserManagementType } from "@/app/reports/services";
import { TABLE_PAGE_SIZE } from "@/app/shared/utils/constants";
import { getDailyManagementColumns } from "@/app/reports/utils/tableColumns/customerSupport";
import { formatUserManagementData } from "@/app/reports/utils/helpers";

import EntityPicker from "@/app/shared/components/pickers/EntityPicker.vue";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import BasesPicker from "@/app/shared/components/pickers/BasesPicker.vue";
import { useGlobalDownloadState } from "@/app/shared/composables/useGlobalDownloadState";

const store = useStore();

const user = computed(() => store.getters["getDataUser"]);
const { scrollStyle } = useScrollResize();

// Form
const formState = reactive({
  portfolio: null,
  month: null,
  type: null,
});
const isButtonDisabled = computed(() => {
  for (const key in formState) {
    if (!formState[key]) return true;
  }

  return false;
});
const isDownloading = ref(false);
const { isGlobalDownloading } = useGlobalDownloadState();

// Table
const reportData = ref(null);
const currentTablePage = ref(1);
const tableColumns = ref([]);

const isTableLoading = ref(false);
const loadingSearch = ref(false);
const pagination = computed(() => ({
  total: reportData.value?.count,
  current: currentTablePage.value,
  pageSize: TABLE_PAGE_SIZE,
  showSizeChanger: false,
}));

const handleTableChange = async (page) => {
  currentTablePage.value = page.current;
  await fetchReportData();
};

const fetchReportData = async () => {
  isTableLoading.value = true;

  const response = await fetchUserManagementType({
    filters: formState,
    page: currentTablePage.value,
  });
  reportData.value = {
    ...response,
    results: formatUserManagementData({
      rawData: response?.results,
      valueType: formState.type,
    }),
  };
  tableColumns.value = getDailyManagementColumns(formState.month || "", { byUser: true });

  isTableLoading.value = false;
};

const handleSearch = async () => {
  if (loadingSearch.value) return;

  try {
    loadingSearch.value = true;
    currentTablePage.value = 1;
    await fetchReportData();
  } catch (error) {
    console.log("Error handleSearch UsersManagement", error);
  } finally {
    loadingSearch.value = false;
  }
};

const handleDownload = async () => {
  isDownloading.value = true;
  await downloadUserManagementType({ filters: formState });
  isDownloading.value = false;
};
</script>

<style lang="sass" scoped>
header
  display: flex
  justify-content: space-between
  align-items: flex-end
  margin: 15px 0

.form
  display: flex
  gap: 10px

.ant-form-item
  margin: 0
  width: 180px

:deep(.ant-form-item-label)
  padding: 0

.actions
  display: flex
  align-items: center
  gap: 10px
</style>
